import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './general';
import {
    ExpenseAreaItem,
    ExpenseAreasI,
    LinkedComponentsI,
    SupplierProfileQuestionaryComponentsI,
    SupplierProfileQuestionaryData
} from './types/expenseareas';

export const expenseAresApi = createApi({
    reducerPath: 'expenseAresApi',
    baseQuery,
    endpoints: (builder) => ({
        getExpenseAreaList: builder.query<ExpenseAreasI, any>({
            query: (params) => ({
                url: 'admin/tenders/expense-areas',
                method: 'GET',
                params
            })
        }),

        getExpenseArea: builder.query<ExpenseAreaItem, any>({
            query: (id) => ({
                url: `admin/tenders/expense-areas/${id}`,
                method: 'GET'
            })
        }),
        ChangeStatus: builder.mutation({
            query: (id) => {
                return {
                    url: `/admin/tenders/expense-areas/${id}/change-status`,
                    method: 'PUT'
                };
            }
        }),
        deleteExpenseArea: builder.mutation({
            query: (id) => ({
                url: `/admin/tenders/expense-areas/${id}`,
                method: 'DELETE'
            })
        }),
        createExpenseArea: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `/admin/tenders/expense-areas`,
                    method: 'POST',
                    body: data?.data
                };
            }
        }),

        updateExpenseArea: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `/admin/tenders/expense-areas/${data?.id}`,
                    method: 'PUT',
                    body: data?.data
                };
            }
        }),
        getBuilderComponents: builder.query<SupplierProfileQuestionaryComponentsI, any>({
            query: (data) => ({
                url: `admin/tenders/expense-areas/${data?.id}/forms/${data?.type}/components`,
                method: 'GET'
            })
        }),
        getLinkedComponents: builder.query<LinkedComponentsI, any>({
            query: (data) => ({
                url: `admin/tenders/expense-areas/${data?.id}/forms/${data?.type}/linked-components`,
                method: 'GET'
            })
        }),
        showBuilderData: builder.query<SupplierProfileQuestionaryData, any>({
            query: (data) => ({
                url: `admin/tenders/expense-areas/${data?.id}/forms/${data?.type}/show`,
                method: 'GET'
            }),
            keepUnusedDataFor: 0
        }),

        saveAsDraftForm: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `/admin/tenders/expense-areas/${data?.expense_area_id}/forms/${data?.type}/draft`,
                    method: 'POST',
                    body: data?.data
                };
            }
        }),

        publishForm: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `/admin/tenders/expense-areas/${data?.expense_area_id}/forms/${data?.type}/publish`,
                    method: 'POST',
                    body: data?.data
                };
            }
        }),
        copyEA: builder.mutation<any, any>({
            query: (id) => {
                return {
                    url: `/admin/tenders/expense-areas/${id}/copy`,
                    method: 'POST'
                };
            }
        }),
        previewFormDiscardChanges: builder.mutation<any, { expense_area_id: number; type: string }>({
            query: (data) => ({
                url: `/admin/tenders/expense-areas/${data.expense_area_id}/forms/${data.type}/preview/discard-changes`,
                method: 'POST'
            })
        }),
        getKickOffPreviewForm: builder.query<any, { expense_area_id: number; step?: number | null }>({
            query: (data) => ({
                url: `/admin/tenders/expense-areas/${data.expense_area_id}/forms/kick-off-questionnaire/preview`,
                method: 'GET',
                params: data.step != null ? { step: data.step } : undefined
            }),
            keepUnusedDataFor: 0
        }),
        saveKickOffPreviewForm: builder.mutation<
            any,
            { expense_area_id: number; step?: number | string | null; action?: string; formData: any }
        >({
            query: (data) => {
                const params = new URLSearchParams();
                if (data.step != null && data.step !== '') params.append('step', String(data.step));
                if (data.action) params.append('action', data.action);
                const qs = params.toString();
                return {
                    url: `/admin/tenders/expense-areas/${data.expense_area_id}/forms/kick-off-questionnaire/preview${
                        qs ? `?${qs}` : ''
                    }`,
                    method: 'POST',
                    body: data.formData
                };
            }
        }),
        getSupplierProfilePreviewQuestionary: builder.query<any, { expense_area_id: number }>({
            query: (data) => ({
                url: `/admin/tenders/expense-areas/${data.expense_area_id}/forms/supplier-profile-questionnaire/preview`,
                method: 'GET'
            }),
            keepUnusedDataFor: 0
        }),
        saveSupplierProfilePreviewForm: builder.mutation<
            any,
            {
                expense_area_id: number;
                formData: any;
            }
        >({
            query: (data) => ({
                url: `/admin/tenders/expense-areas/${data.expense_area_id}/forms/supplier-profile-questionnaire/preview`,
                method: 'POST',
                body: data.formData
            })
        }),
        syncSupplierProfilePreviewComponents: builder.mutation<any, { expense_area_id: number; data: any }>({
            query: (data) => ({
                url: `/admin/tenders/expense-areas/${data.expense_area_id}/forms/supplier-profile-questionnaire/preview/sync-components`,
                method: 'POST',
                body: data.data
            })
        }),
        getScoreCardPreviewQuestionary: builder.query<any, { expense_area_id: number }>({
            query: (data) => ({
                url: `/admin/tenders/expense-areas/${data.expense_area_id}/forms/score-card/preview`,
                method: 'GET'
            }),
            keepUnusedDataFor: 0
        }),
        saveScoreCardPreviewForm: builder.mutation<
            any,
            {
                expense_area_id: number;
                formData: any;
            }
        >({
            query: (data) => ({
                url: `/admin/tenders/expense-areas/${data.expense_area_id}/forms/score-card/preview`,
                method: 'POST',
                body: data.formData
            })
        }),
        syncScoreCardPreviewComponents: builder.mutation<any, { expense_area_id: number; data: any }>({
            query: (data) => ({
                url: `/admin/tenders/expense-areas/${data.expense_area_id}/forms/score-card/preview/sync-components`,
                method: 'POST',
                body: data.data
            })
        })
    })
});

export const {
    useGetExpenseAreaListQuery,
    useChangeStatusMutation,
    useDeleteExpenseAreaMutation,
    useCreateExpenseAreaMutation,
    useLazyGetExpenseAreaQuery,
    useUpdateExpenseAreaMutation,
    useLazyShowBuilderDataQuery,
    useLazyGetBuilderComponentsQuery,
    useSaveAsDraftFormMutation,
    usePublishFormMutation,
    useLazyGetLinkedComponentsQuery,
    useCopyEAMutation,
    usePreviewFormDiscardChangesMutation,
    useLazyGetKickOffPreviewFormQuery,
    useSaveKickOffPreviewFormMutation,
    useLazyGetSupplierProfilePreviewQuestionaryQuery,
    useSaveSupplierProfilePreviewFormMutation,
    useSyncSupplierProfilePreviewComponentsMutation,
    useLazyGetScoreCardPreviewQuestionaryQuery,
    useSaveScoreCardPreviewFormMutation,
    useSyncScoreCardPreviewComponentsMutation
} = expenseAresApi;
